import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { folderService } from "../services/folderService";
import type { TreeFolder } from "../types";
import type { DocumentFolder } from "../types";
import { useToast } from "@/hooks/use-toast";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";

interface UseCustomFoldersProps {
  buildingId: string;
  projectId?: string;
  userId: string;
  orgId: string;
}

export function useCustomFolders({ buildingId, projectId, userId, orgId }: UseCustomFoldersProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const documentStore = useDocumentStore();
  const foldersQueryKey = ["custom-folders", buildingId, projectId];

  // Query for project-scoped custom folders
  const {
    data: folders = [],
    isLoading: foldersLoading,
    error: foldersError,
    refetch: refetchFolders,
  } = useQuery({
    queryKey: foldersQueryKey,
    queryFn: () => {
      if (!projectId) return [];
      return folderService.getProjectFolders(buildingId, projectId);
    },
    enabled: !!buildingId && !!projectId,
  });

  // Build hierarchical tree
  const folderTree = folderService.buildEnhancedFolderTree(folders);

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: async ({
      name,
      category,
      parentId,
    }: {
      name: string;
      category: "documents" | "drawings";
      parentId?: string;
    }) => {
      if (!projectId) {
        throw new Error("Project must be selected to create custom folders");
      }
      return folderService.createCustomFolder(
        buildingId,
        name,
        category,
        userId,
        orgId,
        parentId,
        projectId
      );
    },
    onSuccess: () => {
      refetchFolders();
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast({
        title: "Success",
        description: "Folder created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update folder mutation
  const updateFolderMutation = useMutation({
    mutationFn: async ({
      folderId,
      updates,
    }: {
      folderId: string;
      updates: Partial<{ name: string; parent_id: string | null }>;
    }) => {
      return folderService.updateFolder(folderId, updates, userId);
    },
    onSuccess: (updatedFolder: DocumentFolder, { folderId, updates }) => {
      // Write the saved row straight into the cache instead of only asking for
      // a refetch: every surface that renders a folder name (the row, the tree
      // sidebar, the breadcrumb, the selected-folder header) reads this entry,
      // so they all show the new name on the next paint rather than after the
      // network round-trip.
      queryClient.setQueryData(foldersQueryKey, (current?: DocumentFolder[]) =>
        current?.map((folder) =>
          String(folder.id) === String(folderId) ? { ...folder, ...updatedFolder } : folder
        )
      );
      // Document rows render their folder chip and breadcrumb from the MST tag
      // map, which only loadTags fills — without this a rename stayed invisible
      // there until a full page reload.
      if (updates.name) documentStore.renameFolderTag(folderId, updates.name);
      if (updates.parent_id !== undefined) documentStore.moveFolderTag(folderId, updates.parent_id);
      refetchFolders();
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast({
        title: "Success",
        description: "Folder updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Move folder mutation
  const moveFolderMutation = useMutation({
    mutationFn: async ({
      folderId,
      newParentId,
    }: {
      folderId: string;
      newParentId: string | null;
      /** Set by a caller moving several folders at once, which reports the
       *  whole batch itself instead of stacking one toast per folder. */
      silent?: boolean;
    }) => {
      return folderService.moveFolder(folderId, newParentId, userId);
    },
    onSuccess: (_folder: DocumentFolder, { folderId, newParentId, silent }) => {
      // Same MST sync as a rename: the tag map otherwise keeps the old
      // ancestor chain until a full page reload.
      documentStore.moveFolderTag(folderId, newParentId);
      refetchFolders();
      if (silent) return;
      toast({
        title: "Success",
        description: "Folder moved successfully",
      });
    },
    onError: (error: Error, { silent }) => {
      // A silent caller moves several folders in one gesture and reports the
      // batch once; toasting per failed folder would stack N destructive
      // toasts under that one. The rejection still reaches it via mutateAsync.
      if (silent) return;
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: async (folderId: string) => {
      return folderService.deleteFolder(folderId, userId);
    },
    onSuccess: () => {
      refetchFolders();
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast({
        title: "Success",
        description: "Folder deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Move document to folder mutation
  const moveDocumentMutation = useMutation({
    mutationFn: async ({
      documentId,
      folderId,
    }: {
      documentId: string;
      folderId: string | null;
    }) => {
      return folderService.moveDocumentToFolder(documentId, folderId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      refetchFolders();
      toast({
        title: "Success",
        description: "Document moved successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Helper functions
  const findFolderById = (folderId: string): TreeFolder | null => {
    const findInTree = (folders: TreeFolder[]): TreeFolder | null => {
      for (const folder of folders) {
        if (folder.id === folderId) return folder;
        const found = findInTree(folder.children);
        if (found) return found;
      }
      return null;
    };
    return findInTree(folderTree);
  };

  const getFolderPath = (folderId: string): TreeFolder[] => {
    const path: TreeFolder[] = [];
    const findPath = (
      folders: TreeFolder[],
      targetId: string,
      currentPath: TreeFolder[]
    ): boolean => {
      for (const folder of folders) {
        const newPath = [...currentPath, folder];
        if (folder.id === targetId) {
          path.push(...newPath);
          return true;
        }
        if (findPath(folder.children, targetId, newPath)) {
          return true;
        }
      }
      return false;
    };
    findPath(folderTree, folderId, []);
    return path;
  };

  const canCreateFolders = !!projectId;

  return {
    // Data
    folders,
    folderTree,
    isLoading: foldersLoading,
    /**
     * The failure of the last fetch, once react-query has spent its retries.
     * Nothing else refetches this query, so a caller that blocks on it must
     * offer the user a way back. `folders` may still hold the list from an
     * earlier successful fetch. A new attempt yields a new error object, so
     * this is also the signal that a retry failed again.
     */
    foldersError,
    canCreateFolders,

    // Actions
    createFolder: createFolderMutation.mutate,
    updateFolder: updateFolderMutation.mutate,
    moveFolder: moveFolderMutation.mutate,
    /**
     * Awaitable move. `moveFolder` is `mutate`: it returns `undefined` and
     * swallows failures into the error toast, so `await moveFolder(...)`
     * resolves before the request even leaves. Callers that refetch after a
     * move, or that report success themselves, must use this one.
     */
    moveFolderAsync: moveFolderMutation.mutateAsync,
    deleteFolder: deleteFolderMutation.mutate,
    moveDocument: moveDocumentMutation.mutate,
    refetchFolders,

    // Loading states
    isCreating: createFolderMutation.isPending,
    isUpdating: updateFolderMutation.isPending,
    isMoving: moveFolderMutation.isPending,
    isDeleting: deleteFolderMutation.isPending,
    isMovingDocument: moveDocumentMutation.isPending,

    // Helpers
    findFolderById,
    getFolderPath,
  };
}
